import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useIsAdmin } from '@/components/auth/useIsAdmin';
import { AdminOnlyEmptyState } from '@/components/auth/admin-only-empty-state';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, 
  Shield, 
  Users as UsersIcon, 
  FileText, 
  Settings, 
  CheckCircle2,
  XCircle,
  DollarSign,
  TrendingUp,
  Briefcase,
  HandshakeIcon,
  Users,
  Eye,
  Edit,
  UserCog,
  Download,
  Calendar,
  ChevronLeft,
  ChevronRight,
  History,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { type UserPermissions, DEFAULT_PERMISSIONS, ROLE_DESCRIPTIONS } from '@/types/user';
import { format } from 'date-fns';

const MAX_NAME_LENGTH = 100;
const MAX_DESCRIPTION_LENGTH = 500;

interface RoleFormData {
  name: string;
  description: string;
  roleType: 'custom';
  status: 'active' | 'inactive';
  permissions: UserPermissions;
}

interface CustomRole {
  id: string;
  name: string;
  description: string;
  permissions: UserPermissions;
  userCount: number;
  isSystem: boolean;
  createdAt: string;
}

// Mock system roles data
const SYSTEM_ROLES: Record<string, CustomRole> = {
  administrator: {
    id: 'administrator',
    name: 'Administrator',
    description: ROLE_DESCRIPTIONS.administrator,
    isSystem: true,
    permissions: DEFAULT_PERMISSIONS.administrator,
    userCount: 0,
    createdAt: '2024-01-01T00:00:00Z',
  },
  user: {
    id: 'user',
    name: 'User',
    description: ROLE_DESCRIPTIONS.user,
    isSystem: true,
    permissions: DEFAULT_PERMISSIONS.user,
    userCount: 0,
    createdAt: '2024-01-01T00:00:00Z',
  },
  view_only: {
    id: 'view_only',
    name: 'View Only',
    description: ROLE_DESCRIPTIONS.view_only,
    isSystem: true,
    permissions: DEFAULT_PERMISSIONS.view_only,
    userCount: 0,
    createdAt: '2024-01-01T00:00:00Z',
  },
};

export function DuplicateRolePage() {
  const { isAdmin, isLoading: isAdminLoading } = useIsAdmin();
  const navigate = useNavigate();
  const { roleId } = useParams<{ roleId: string }>();
  const location = useLocation();
  const { duplicateFrom, newRoleName } = location.state || {};

  // Get the original role data
  const originalRole = duplicateFrom || SYSTEM_ROLES[roleId || ''];

  // Form state - Initialize with duplicated data
  const [formData, setFormData] = useState<RoleFormData>({
    name: newRoleName || `${originalRole?.name || ''} (Copy)`,
    description: originalRole?.description || '',
    roleType: 'custom',
    status: 'active',
    permissions: originalRole?.permissions || DEFAULT_PERMISSIONS.view_only,
  });

  const [originalData] = useState<RoleFormData>(formData);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [nameInputRef, setNameInputRef] = useState<HTMLInputElement | null>(null);

  // Auto-focus and select the role name field
  useEffect(() => {
    if (nameInputRef) {
      nameInputRef.focus();
      nameInputRef.select();
    }
  }, [nameInputRef]);

  // Check for unsaved changes
  useEffect(() => {
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    setHasUnsavedChanges(hasChanges);
  }, [formData, originalData]);

  // Handle browser back/forward
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Role name is required';
    } else if (formData.name.length > MAX_NAME_LENGTH) {
      newErrors.name = `Role name cannot exceed ${MAX_NAME_LENGTH} characters`;
    }
    // In real app, check for duplicate names against backend
    const systemRoleNames = ['administrator', 'user', 'view only'];
    if (systemRoleNames.includes(formData.name.toLowerCase())) {
      newErrors.name = 'A role with this name already exists';
    }

    if (formData.description.length > MAX_DESCRIPTION_LENGTH) {
      newErrors.description = `Description cannot exceed ${MAX_DESCRIPTION_LENGTH} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));

      // In a real app, this would save to the backend
      toast.success(`✓ Role '${formData.name}' created successfully`);
      
      // Navigate back to roles list
      navigate('/settings/users?tab=roles');
    } catch (error) {
      toast.error('Failed to create role. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    } else {
      navigate('/settings/users?tab=roles');
    }
  };

  const confirmNavigation = () => {
    setShowUnsavedDialog(false);
    navigate('/settings/users?tab=roles');
  };

  const cancelNavigation = () => {
    setShowUnsavedDialog(false);
  };

  const handleFieldChange = (field: keyof RoleFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePermissionChange = (permission: keyof UserPermissions, value: any) => {
    setFormData(prev => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        [permission]: value,
      },
    }));
  };

  const isFormValid = formData.name.trim() && Object.keys(errors).length === 0;
  
  // Check if no permissions are assigned
  const hasNoPermissions = !Object.values(formData.permissions).some(value => 
    value === true || (typeof value === 'string' && value !== 'none')
  );

  // Count assigned permissions
  const permissionCount = Object.values(formData.permissions).filter(value => 
    value === true || (typeof value === 'string' && value !== 'none')
  ).length;

  // Generate audit log entry
  const auditEntry = {
    timestamp: new Date().toISOString(),
    user: 'Current User', // In real app, get from auth context
    action: `Role created by duplicating '${originalRole?.name || 'Unknown Role'}'`,
  };

  if (isAdminLoading) {
    return (
      <div className="flex min-h-[50vh] w-full items-center justify-center">
        <output
          aria-label="Loading"
          className="block h-8 w-8 animate-spin rounded-full border-2 border-muted-foreground/30 border-t-foreground"
        />
      </div>
    );
  }
  if (!isAdmin) return <AdminOnlyEmptyState />;

  if (!originalRole) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Role Not Found</h2>
          <p className="text-muted-foreground mb-4">The role you're trying to duplicate doesn't exist.</p>
          <Button onClick={() => navigate('/settings/users?tab=roles')}>
            Back to Roles
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header with Breadcrumb */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <button
              onClick={() => navigate('/settings/users?tab=roles')}
              className="hover:text-foreground transition-colors"
            >
              Settings
            </button>
            <ChevronRight className="w-4 h-4" />
            <button
              onClick={() => navigate('/settings/users?tab=users')}
              className="hover:text-foreground transition-colors"
            >
              Users
            </button>
            <ChevronRight className="w-4 h-4" />
            <button
              onClick={() => navigate('/settings/users?tab=roles')}
              className="hover:text-foreground transition-colors"
            >
              Roles & Permissions
            </button>
            <ChevronRight className="w-4 h-4" />
            <span className="text-foreground">{formData.name}</span>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCancel}
                className="gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </Button>
              <div className="flex items-center gap-3">
                <Shield className="w-8 h-8 text-primary" />
                <div>
                  <h1 className="text-2xl font-bold">{formData.name}</h1>
                  <p className="text-sm text-muted-foreground">
                    Duplicated from "{originalRole.name}"
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={!isFormValid || isSaving}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Role'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Warning for no permissions */}
      {hasNoPermissions && formData.name.trim() && (
        <div className="container mx-auto px-6 pt-4">
          <Alert variant="warning" className="border-orange-200 bg-orange-50 dark:bg-orange-950/20">
            <AlertDescription className="text-orange-800 dark:text-orange-200">
              ⚠️ Warning: This role has no permissions assigned. Users with this role will have no access.
            </AlertDescription>
          </Alert>
        </div>
      )}

      {/* Main Content */}
      <div className="container mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-9 lg:w-auto lg:inline-grid">
            <TabsTrigger value="overview" className="gap-2">
              <FileText className="w-4 h-4" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="customer-accounts" className="gap-2">
              <Users className="w-4 h-4" />
              Customer Accounts
            </TabsTrigger>
            <TabsTrigger value="payments" className="gap-2">
              <DollarSign className="w-4 h-4" />
              Payments
            </TabsTrigger>
            <TabsTrigger value="collections" className="gap-2">
              <Briefcase className="w-4 h-4" />
              Collections
            </TabsTrigger>
            <TabsTrigger value="disputes" className="gap-2">
              <HandshakeIcon className="w-4 h-4" />
              Disputes
            </TabsTrigger>
            <TabsTrigger value="analytics" className="gap-2">
              <TrendingUp className="w-4 h-4" />
              Analytics & Reporting
            </TabsTrigger>
            <TabsTrigger value="administration" className="gap-2">
              <Settings className="w-4 h-4" />
              Administration
            </TabsTrigger>
            <TabsTrigger value="users" className="gap-2">
              <UsersIcon className="w-4 h-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="audit" className="gap-2">
              <History className="w-4 h-4" />
              Audit
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Role Details</CardTitle>
                <CardDescription>
                  Basic information about this role
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-6 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="role-name">
                      Role Name <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="role-name"
                      ref={setNameInputRef}
                      placeholder="Enter role name..."
                      value={formData.name}
                      onChange={(e) => handleFieldChange('name', e.target.value)}
                      maxLength={MAX_NAME_LENGTH}
                      className={errors.name ? 'border-destructive' : ''}
                    />
                    <div className="flex items-center justify-between">
                      {errors.name ? (
                        <p className="text-sm text-destructive">{errors.name}</p>
                      ) : (
                        <p className="text-sm text-muted-foreground">
                          {formData.name.length}/{MAX_NAME_LENGTH}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="role-type">Role Type</Label>
                    <Input
                      id="role-type"
                      value="Custom Role"
                      disabled
                      className="bg-muted"
                    />
                    <p className="text-sm text-muted-foreground">
                      Always Custom, even when duplicating a System Role
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="role-description">
                    Role Description
                  </Label>
                  <Textarea
                    id="role-description"
                    placeholder="Describe the purpose and responsibilities..."
                    value={formData.description}
                    onChange={(e) => handleFieldChange('description', e.target.value)}
                    maxLength={MAX_DESCRIPTION_LENGTH}
                    rows={4}
                    className={errors.description ? 'border-destructive' : ''}
                  />
                  <div className="flex items-center justify-between">
                    {errors.description ? (
                      <p className="text-sm text-destructive">{errors.description}</p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {formData.description.length}/{MAX_DESCRIPTION_LENGTH}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="space-y-0.5">
                    <Label htmlFor="status">Status</Label>
                    <p className="text-sm text-muted-foreground">
                      Control whether this role is active
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium">
                      {formData.status === 'active' ? 'Active' : 'Inactive'}
                    </span>
                    <Switch
                      id="status"
                      checked={formData.status === 'active'}
                      onCheckedChange={(checked) =>
                        handleFieldChange('status', checked ? 'active' : 'inactive')
                      }
                    />
                  </div>
                </div>

                <div className="p-4 border rounded-lg space-y-2">
                  <div className="flex items-center gap-2">
                    <UsersIcon className="w-4 h-4 text-muted-foreground" />
                    <Label>Users Assigned</Label>
                  </div>
                  <p className="text-2xl font-semibold">0 users assigned</p>
                  <p className="text-sm text-muted-foreground">
                    Users from the original role are not copied
                  </p>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Permissions Summary</CardTitle>
                <CardDescription>
                  All permissions copied from "{originalRole.name}"
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <span className="text-sm font-medium">Total Permissions Assigned</span>
                    <Badge variant="secondary">{permissionCount} permissions</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    All permission settings have been copied exactly as configured in the original role. 
                    You can modify any permissions in the respective tabs.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Customer Accounts Tab */}
          <TabsContent value="customer-accounts" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Customer Accounts Permissions</CardTitle>
                <CardDescription>
                  Control access to customer account information and management
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionRadioGroup
                  label="View Customer Accounts"
                  description="Determines which customer accounts users can view"
                  value={String(formData.permissions.customerAccountsView || 'none')}
                  onChange={(value) => handlePermissionChange('customerAccountsView', value)}
                  options={[
                    { value: 'none', label: 'No Access', description: 'Cannot view any customer accounts' },
                    { value: 'assigned_only', label: 'Assigned Only', description: 'Can only view accounts assigned to them' },
                    { value: 'assigned_default', label: 'All (Assigned by Default)', description: 'Can view all accounts, but assigned accounts appear by default' },
                    { value: 'all', label: 'All', description: 'Can view all customer accounts' },
                  ]}
                />

                <PermissionRadioGroup
                  label="Edit Customer Accounts"
                  description="Determines which customer accounts users can edit"
                  value={String(formData.permissions.customerAccountsEdit || 'none')}
                  onChange={(value) => handlePermissionChange('customerAccountsEdit', value)}
                  options={[
                    { value: 'none', label: 'No Access', description: 'Cannot edit any customer accounts' },
                    { value: 'assigned', label: 'Assigned Only', description: 'Can only edit accounts assigned to them' },
                    { value: 'all', label: 'All', description: 'Can edit all customer accounts' },
                  ]}
                />

                <PermissionCheckbox
                  label="Reassign Customer Accounts"
                  description="Allow users to reassign customer accounts to other team members"
                  checked={!!formData.permissions.customerAccountsReassign}
                  onChange={(checked) => handlePermissionChange('customerAccountsReassign', checked)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Payments Permissions</CardTitle>
                <CardDescription>
                  Control access to payment viewing and allocation functions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View Payments"
                  description="Allow users to view payment records and transaction history"
                  checked={!!formData.permissions.viewPayments}
                  onChange={(checked) => handlePermissionChange('viewPayments', checked)}
                />

                <PermissionCheckbox
                  label="Allocate Payments"
                  description="Allow users to allocate payments to invoices"
                  checked={!!formData.permissions.allocatePayments}
                  onChange={(checked) => handlePermissionChange('allocatePayments', checked)}
                />

                <PermissionCheckbox
                  label="Reverse Allocations"
                  description="Allow users to reverse previously allocated payments"
                  checked={!!formData.permissions.reverseAllocations}
                  onChange={(checked) => handlePermissionChange('reverseAllocations', checked)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Collections Tab */}
          <TabsContent value="collections" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Collection Cases Permissions</CardTitle>
                <CardDescription>
                  Control access to collection case management
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionRadioGroup
                  label="View Collection Cases"
                  description="Determines which collection cases users can view"
                  value={String(formData.permissions.collectionCasesView || 'none')}
                  onChange={(value) => handlePermissionChange('collectionCasesView', value)}
                  options={[
                    { value: 'none', label: 'No Access', description: 'Cannot view any collection cases' },
                    { value: 'assigned_only', label: 'Assigned Only', description: 'Can only view cases assigned to them' },
                    { value: 'assigned_default', label: 'All (Assigned by Default)', description: 'Can view all cases, but assigned cases appear by default' },
                    { value: 'all', label: 'All', description: 'Can view all collection cases' },
                  ]}
                />

                <PermissionCheckbox
                  label="Create Collection Cases"
                  description="Allow users to create new collection cases"
                  checked={!!formData.permissions.collectionCasesCreate}
                  onChange={(checked) => handlePermissionChange('collectionCasesCreate', checked)}
                />

                <PermissionRadioGroup
                  label="Edit Collection Cases"
                  description="Determines which collection cases users can edit"
                  value={String(formData.permissions.collectionCasesEdit || 'none')}
                  onChange={(value) => handlePermissionChange('collectionCasesEdit', value)}
                  options={[
                    { value: 'none', label: 'No Access', description: 'Cannot edit any collection cases' },
                    { value: 'assigned', label: 'Assigned Only', description: 'Can only edit cases assigned to them' },
                    { value: 'all', label: 'All', description: 'Can edit all collection cases' },
                  ]}
                />

                <PermissionCheckbox
                  label="Delete Collection Cases"
                  description="Allow users to delete collection cases"
                  checked={!!formData.permissions.collectionCasesDelete}
                  onChange={(checked) => handlePermissionChange('collectionCasesDelete', checked)}
                />

                <PermissionCheckbox
                  label="Reassign Collection Cases"
                  description="Allow users to reassign collection cases to other team members"
                  checked={!!formData.permissions.collectionCasesReassign}
                  onChange={(checked) => handlePermissionChange('collectionCasesReassign', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Promise to Pay Permissions</CardTitle>
                <CardDescription>
                  Control access to promise to pay management
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionRadioGroup
                  label="View Promises to Pay"
                  description="Determines which promises to pay users can view"
                  value={String(formData.permissions.promiseToPayView || 'none')}
                  onChange={(value) => handlePermissionChange('promiseToPayView', value)}
                  options={[
                    { value: 'none', label: 'No Access', description: 'Cannot view any promises to pay' },
                    { value: 'assigned_only', label: 'Assigned Only', description: 'Can only view promises assigned to them' },
                    { value: 'assigned_default', label: 'All (Assigned by Default)', description: 'Can view all promises, but assigned promises appear by default' },
                    { value: 'all', label: 'All', description: 'Can view all promises to pay' },
                  ]}
                />

                <PermissionCheckbox
                  label="Create Promises to Pay"
                  description="Allow users to create new promises to pay"
                  checked={!!formData.permissions.promiseToPayCreate}
                  onChange={(checked) => handlePermissionChange('promiseToPayCreate', checked)}
                />

                <PermissionRadioGroup
                  label="Edit Promises to Pay"
                  description="Determines which promises to pay users can edit"
                  value={String(formData.permissions.promiseToPayEdit || 'none')}
                  onChange={(value) => handlePermissionChange('promiseToPayEdit', value)}
                  options={[
                    { value: 'none', label: 'No Access', description: 'Cannot edit any promises to pay' },
                    { value: 'assigned', label: 'Assigned Only', description: 'Can only edit promises assigned to them' },
                    { value: 'all', label: 'All', description: 'Can edit all promises to pay' },
                  ]}
                />

                <PermissionCheckbox
                  label="Delete Promises to Pay"
                  description="Allow users to delete promises to pay"
                  checked={!!formData.permissions.promiseToPayDelete}
                  onChange={(checked) => handlePermissionChange('promiseToPayDelete', checked)}
                />

                <PermissionCheckbox
                  label="Reassign Promises to Pay"
                  description="Allow users to reassign promises to pay to other team members"
                  checked={!!formData.permissions.promiseToPayReassign}
                  onChange={(checked) => handlePermissionChange('promiseToPayReassign', checked)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Disputes Tab */}
          <TabsContent value="disputes" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Dispute Management Permissions</CardTitle>
                <CardDescription>
                  Control access to dispute tracking and resolution
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <Alert>
                  <AlertDescription>
                    Dispute permissions are currently managed through the "Manage Accounts" permission. 
                    Granular dispute permissions will be available in a future update.
                  </AlertDescription>
                </Alert>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics & Reporting Tab */}
          <TabsContent value="analytics" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Analytics & Reporting Permissions</CardTitle>
                <CardDescription>
                  Control access to dashboards and reporting features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View Credit Control Dashboard"
                  description="Access to main credit control overview and metrics"
                  checked={!!formData.permissions.viewCreditControlDashboard}
                  onChange={(checked) => handlePermissionChange('viewCreditControlDashboard', checked)}
                />

                <PermissionCheckbox
                  label="View Collections Dashboard"
                  description="Access to collections queue and team performance metrics"
                  checked={!!formData.permissions.viewCollectionsDashboard}
                  onChange={(checked) => handlePermissionChange('viewCollectionsDashboard', checked)}
                />

                <PermissionCheckbox
                  label="View Disputes Dashboard"
                  description="Access to dispute tracking and analytics"
                  checked={!!formData.permissions.viewDisputesDashboard}
                  onChange={(checked) => handlePermissionChange('viewDisputesDashboard', checked)}
                />

                <PermissionCheckbox
                  label="View Ageing Analysis"
                  description="Access to accounts receivable ageing reports"
                  checked={!!formData.permissions.viewAgeingAnalysis}
                  onChange={(checked) => handlePermissionChange('viewAgeingAnalysis', checked)}
                />

                <PermissionCheckbox
                  label="View Multi-Company Dashboard"
                  description="Access to consolidated multi-company view"
                  checked={!!formData.permissions.viewMultiCompanyDashboard}
                  onChange={(checked) => handlePermissionChange('viewMultiCompanyDashboard', checked)}
                />

                <PermissionCheckbox
                  label="Access Manager Views"
                  description="Can switch between user views and access manager-level functionality"
                  checked={!!formData.permissions.accessManagerViews}
                  onChange={(checked) => handlePermissionChange('accessManagerViews', checked)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Administration Tab */}
          <TabsContent value="administration" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  Permissions for managing user accounts
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View user account management"
                  checked={!!formData.permissions.adminManageUsersView}
                  onChange={(checked) => handlePermissionChange('adminManageUsersView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create new user accounts"
                  checked={!!formData.permissions.adminManageUsersCreate}
                  onChange={(checked) => handlePermissionChange('adminManageUsersCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit existing user accounts"
                  checked={!!formData.permissions.adminManageUsersEdit}
                  onChange={(checked) => handlePermissionChange('adminManageUsersEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete user accounts"
                  checked={!!formData.permissions.adminManageUsersDelete}
                  onChange={(checked) => handlePermissionChange('adminManageUsersDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Company Settings</CardTitle>
                <CardDescription>
                  Permissions for managing company configurations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View company settings"
                  checked={!!formData.permissions.adminManageCompaniesView}
                  onChange={(checked) => handlePermissionChange('adminManageCompaniesView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create new companies"
                  checked={!!formData.permissions.adminManageCompaniesCreate}
                  onChange={(checked) => handlePermissionChange('adminManageCompaniesCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit company configurations"
                  checked={!!formData.permissions.adminManageCompaniesEdit}
                  onChange={(checked) => handlePermissionChange('adminManageCompaniesEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete companies"
                  checked={!!formData.permissions.adminManageCompaniesDelete}
                  onChange={(checked) => handlePermissionChange('adminManageCompaniesDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>General Settings</CardTitle>
                <CardDescription>
                  Permissions for managing general system settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View general system settings"
                  checked={!!formData.permissions.adminManageGeneralSettingsView}
                  onChange={(checked) => handlePermissionChange('adminManageGeneralSettingsView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create general settings"
                  checked={!!formData.permissions.adminManageGeneralSettingsCreate}
                  onChange={(checked) => handlePermissionChange('adminManageGeneralSettingsCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit general settings"
                  checked={!!formData.permissions.adminManageGeneralSettingsEdit}
                  onChange={(checked) => handlePermissionChange('adminManageGeneralSettingsEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete general settings"
                  checked={!!formData.permissions.adminManageGeneralSettingsDelete}
                  onChange={(checked) => handlePermissionChange('adminManageGeneralSettingsDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>System Integrations</CardTitle>
                <CardDescription>
                  Permissions for managing system integrations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View system integrations"
                  checked={!!formData.permissions.adminManageIntegrationsView}
                  onChange={(checked) => handlePermissionChange('adminManageIntegrationsView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create new integrations"
                  checked={!!formData.permissions.adminManageIntegrationsCreate}
                  onChange={(checked) => handlePermissionChange('adminManageIntegrationsCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit integration configurations"
                  checked={!!formData.permissions.adminManageIntegrationsEdit}
                  onChange={(checked) => handlePermissionChange('adminManageIntegrationsEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete integrations"
                  checked={!!formData.permissions.adminManageIntegrationsDelete}
                  onChange={(checked) => handlePermissionChange('adminManageIntegrationsDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Communication Templates</CardTitle>
                <CardDescription>
                  Permissions for managing email and communication templates
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View communication templates"
                  checked={!!formData.permissions.adminManageTemplatesView}
                  onChange={(checked) => handlePermissionChange('adminManageTemplatesView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create new templates"
                  checked={!!formData.permissions.adminManageTemplatesCreate}
                  onChange={(checked) => handlePermissionChange('adminManageTemplatesCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit existing templates"
                  checked={!!formData.permissions.adminManageTemplatesEdit}
                  onChange={(checked) => handlePermissionChange('adminManageTemplatesEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete templates"
                  checked={!!formData.permissions.adminManageTemplatesDelete}
                  onChange={(checked) => handlePermissionChange('adminManageTemplatesDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Collection Strategies</CardTitle>
                <CardDescription>
                  Permissions for managing collection strategies and workflows
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View collection strategies"
                  checked={!!formData.permissions.adminManageCollectionStrategiesView}
                  onChange={(checked) => handlePermissionChange('adminManageCollectionStrategiesView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create new strategies"
                  checked={!!formData.permissions.adminManageCollectionStrategiesCreate}
                  onChange={(checked) => handlePermissionChange('adminManageCollectionStrategiesCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit strategy configurations"
                  checked={!!formData.permissions.adminManageCollectionStrategiesEdit}
                  onChange={(checked) => handlePermissionChange('adminManageCollectionStrategiesEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete strategies"
                  checked={!!formData.permissions.adminManageCollectionStrategiesDelete}
                  onChange={(checked) => handlePermissionChange('adminManageCollectionStrategiesDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Dispute Types</CardTitle>
                <CardDescription>
                  Permissions for managing dispute type definitions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View dispute type definitions"
                  checked={!!formData.permissions.adminManageDisputeTypesView}
                  onChange={(checked) => handlePermissionChange('adminManageDisputeTypesView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create new dispute types"
                  checked={!!formData.permissions.adminManageDisputeTypesCreate}
                  onChange={(checked) => handlePermissionChange('adminManageDisputeTypesCreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit dispute type definitions"
                  checked={!!formData.permissions.adminManageDisputeTypesEdit}
                  onChange={(checked) => handlePermissionChange('adminManageDisputeTypesEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete dispute types"
                  checked={!!formData.permissions.adminManageDisputeTypesDelete}
                  onChange={(checked) => handlePermissionChange('adminManageDisputeTypesDelete', checked)}
                />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>AI Configuration</CardTitle>
                <CardDescription>
                  Permissions for managing AI and machine learning settings
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <PermissionCheckbox
                  label="View"
                  description="View AI configuration settings"
                  checked={!!formData.permissions.adminConfigureAIView}
                  onChange={(checked) => handlePermissionChange('adminConfigureAIView', checked)}
                />
                <PermissionCheckbox
                  label="Create"
                  description="Create AI configurations"
                  checked={!!formData.permissions.adminConfigureAICreate}
                  onChange={(checked) => handlePermissionChange('adminConfigureAICreate', checked)}
                />
                <PermissionCheckbox
                  label="Edit"
                  description="Edit AI settings"
                  checked={!!formData.permissions.adminConfigureAIEdit}
                  onChange={(checked) => handlePermissionChange('adminConfigureAIEdit', checked)}
                />
                <PermissionCheckbox
                  label="Delete"
                  description="Delete AI configurations"
                  checked={!!formData.permissions.adminConfigureAIDelete}
                  onChange={(checked) => handlePermissionChange('adminConfigureAIDelete', checked)}
                />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Assigned Users</CardTitle>
                <CardDescription>
                  Users who have been assigned this role
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <UsersIcon className="w-12 h-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No users assigned to this role</h3>
                  <p className="text-sm text-muted-foreground max-w-md">
                    Users from the original role are not copied. You can assign users to this role from the User Management page after saving.
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Audit Tab */}
          <TabsContent value="audit" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Audit Log</CardTitle>
                <CardDescription>
                  History of changes made to this role
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Timestamp</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="text-muted-foreground">
                        {format(new Date(auditEntry.timestamp), 'dd MMM yyyy, HH:mm:ss')}
                      </TableCell>
                      <TableCell className="font-medium">{auditEntry.user}</TableCell>
                      <TableCell>{auditEntry.action}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Are you sure you want to leave?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelNavigation}>Stay</AlertDialogCancel>
            <AlertDialogAction onClick={confirmNavigation} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Leave
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// Helper Components
interface PermissionCheckboxProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

function PermissionCheckbox({ label, description, checked, onChange }: PermissionCheckboxProps) {
  return (
    <div className="flex items-start justify-between gap-4 p-4 border rounded-lg">
      <div className="space-y-1 flex-1">
        <Label className="font-medium">{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        className="mt-1"
      />
    </div>
  );
}

interface PermissionRadioGroupProps {
  label: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{
    value: string;
    label: string;
    description: string;
  }>;
}

function PermissionRadioGroup({ label, description, value, onChange, options }: PermissionRadioGroupProps) {
  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <div className="space-y-1">
        <Label className="font-medium">{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <RadioGroup value={value} onValueChange={onChange} className="space-y-3">
        {options.map((option) => (
          <div key={option.value} className="flex items-start gap-3">
            <RadioGroupItem value={option.value} id={`${label}-${option.value}`} className="mt-1" />
            <div className="space-y-1">
              <Label htmlFor={`${label}-${option.value}`} className="font-medium cursor-pointer">
                {option.label}
              </Label>
              <p className="text-sm text-muted-foreground">{option.description}</p>
            </div>
          </div>
        ))}
      </RadioGroup>
    </div>
  );
}
